import moment from 'moment';
import {teltonika} from '@/assets/js/teltonika';
import {EventSeverity, eventsInfo} from "@/assets/js/events-info";
import i18n from "@/assets/js/vue/i18n";
import {TrafficViolationsRequestType} from "@/assets/js/enums/TrafficViolationsRequestType.ts";
import {createApp} from "vue";
import GeoFenceInfo from "@/components/pages/geo-fences/GeoFenceInfo.vue";
import {GeoFenceType} from "@/assets/js/enums/GeoFenceType.ts";
import {FuelCapStatus} from "@/assets/js/enums/FuelCapStatus.ts";
import {gmLoader} from "@/assets/js/google-maps";
import {TaskStatus} from "@/assets/js/enums/TaskStatus.ts";
import {GeoFenceColor} from "@/assets/js/enums/GeoFenceColor.ts";
import {DashboardDeviceColumn} from "@/assets/js/enums/DashboardDeviceColumn.js";
import {actions, mutations} from "@/assets/js/vue/vuex/actions";
import store from '@/assets/js/vue/vuex';
import router from "@/assets/js/vue/router";

const tryAttribute = (sourceObject, attribute, defaultValue) => {
  if (sourceObject['attributes'] === undefined) {
    return defaultValue;
  }

  return sourceObject['attributes'][attribute] === undefined ? defaultValue : sourceObject['attributes'][attribute];
};

const getEventInfoInternal = (eventsInfo, type) => {
  let eventInfo = eventsInfo[type];
  if (!eventInfo) {
    eventInfo = eventsInfo['unknown'];
  }

  return eventInfo;
};

const bulgarianToEnglishMap = {
  'А': 'A', 'В': 'B', 'Е': 'E', 'К': 'K', 'М': 'M', 'Н': 'H', 'О': 'O',
  'Р': 'P', 'С': 'C', 'Т': 'T', 'У': 'Y', 'Х': 'X'
};

export const dataManipulator = {
  replaceByKey(collection, newElement, byKey = 'id') {
    return [
      ...this.deleteByKey(collection, newElement[byKey], byKey),
      newElement,
    ];
  },
  replaceAllByKey(collection, newCollection, byKey = 'id') {
    let resultCollection = collection;

    newCollection.forEach(el => resultCollection = this.replaceByKey(resultCollection, el, byKey));

    return resultCollection;
  },
  sortByKey(key) {
    return (objectA, objectB) => {
      if (objectA[key] < objectB[key]) {
        return -1;
      }

      if (objectA[key] > objectB[key]) {
        return 1;
      }

      return 0;
    };
  },
  deleteByKey(collection, key, byKey = 'id') {
    return collection.filter(el => el[byKey] !== key);
  },
  dateToString(date) {
    if (date instanceof Date && isNaN(date)) {
      return null;
    }

    return date ? moment(date.toISOString()).format('yyyy-MM-DD') : null;
  },
  dateTimeToString(date, includeSeconds = false) {
    if (date instanceof Date && isNaN(date)) {
      return null;
    }

    let format = 'yyyy-MM-DD HH:mm';
    if (includeSeconds) {
      format += ':ss'
    }
    return date ? moment(date.toISOString()).format(format) : null;
  },
  iso8601DateStringToLocalMoment(date) {
    return moment(date).local();
  },
  timeObjToString(timeObj) {
    return timeObj.hours.toString().padStart(2, '0') + ':' + timeObj.minutes.toString().padStart(2, '0');
  },
  timeStringToObj(timeString) {
    const hourMinuteTime = timeString.split(':');

    if (hourMinuteTime[0] === undefined || hourMinuteTime[1] === undefined) {
      return null;
    }

    return {
      hours: parseInt(hourMinuteTime[0]),
      minutes: parseInt(hourMinuteTime[1]),
      seconds: 0,
    };
  },
  knotToKm(knots) {
    return knots * 1.852;
  },
  millisecondsToTime(milliseconds)  {
    const hours = Math.floor(milliseconds / 1000 / 60 / 60);
    milliseconds -= hours * 60 * 60 * 1000;

    const minutes = Math.floor(milliseconds / 1000 / 60);
    milliseconds -= minutes * 60 * 1000;

    const seconds = Math.floor(milliseconds / 1000);
    milliseconds -= seconds * 1000;

    return {
      hours: hours,
      minutes: minutes,
      seconds: seconds,
    };
  },
  formatDuration(duration, includeMinutes = true, includeSeconds = true) {
    let result = duration.hours.toString().padStart(2, '0');
    if (includeMinutes) {
      result += ':' + duration.minutes.toString().padStart(2, '0');
    }
    if (includeSeconds) {
      result += ':' + duration.seconds.toString().padStart(2, '0');
    }

    return result;
  },
  convertHoursToMinutes(hours) {
    if (hours == null) {
      return null;
    }

    return hours * 60;
  },
  convertHoursToMilliseconds(hours) {
    if (hours == null) {
      return null;
    }

    return this.convertMinutesToMilliseconds(hours * 60);
  },
  convertMinutesToHours(minutes) {
    if (minutes == null) {
      return null;
    }

    return Math.floor(minutes / 60);
  },
  convertMinutesToMilliseconds(minutes) {
    if (minutes == null) {
      return null;
    }

    const seconds = minutes * 60;
    return seconds * 1000;
  },
  convertMillisecondsToMinutes(milliseconds) {
    if (milliseconds == null) {
      return null;
    }

    return Math.floor(milliseconds / 1000 / 60);
  },
  convertSecondsToMinutes(seconds) {
    if (seconds == null) {
      return null;
    }

    return Math.floor(seconds / 60);
  },
  truncateToDecimals(num, dec = 2) {
    const calcDec = Math.pow(10, dec);
    return Math.trunc(num * calcDec) / calcDec;
  },
  formatLicensePlateToEnglishChars(plate) {
    // Replace each Cyrillic character with the corresponding English character
    return plate.split('').map(char => bulgarianToEnglishMap[char] || char).join('');
  },
  getDaysTranslationKey(days) {
    return Number(days) === 1 ? 'generic.day' : 'generic.days';
  },
  deepCopyObject(object) {
    return JSON.parse(JSON.stringify(object));
  },
  getTripsRecordPositionsFromMapData(tripsData) {
    return (tripsData?.mapData?.routes ?? []).map(route => route.positions ?? []);
  },
  ensureCondition(condition, callback) {
    setTimeout(() => {
      if (condition?.()) {
        callback?.();
      }
      else {
        this.ensureCondition(condition, callback);
      }
    }, 100);
  }
};

export const entityDataManager = {
  getById(collection, id) {
    return collection?.filter(item => item?.id === id)[0] || null;
  },
  getEntityByField(collection, field, value, formatFieldCallback = null) {
    return collection.filter(item => {
      if (item[field] === undefined) {
        return false;
      }

      let fieldValue = item[field];
      if (formatFieldCallback) {
        fieldValue = formatFieldCallback(item[field]);
      }

      return fieldValue === value;
    })[0] || null;
  },
  getCompanyByUserId(companies, userId) {
    return companies.find(item => item?.assignedUsers?.some(assignedUser => assignedUser?.userId === userId));
  },
  getDeviceByVehicle(devices, vehicle) {
    return devices.filter(device => device.id === vehicle.deviceId)[0] || null;
  },
  getDevicesByGroupId(devices, groupId) {
    if (groupId === null) {
      return [];
    }

    return devices.filter(device => device.groupId === groupId);
  },
  getVehicleByDeviceId(vehicles, deviceId) {
    return this.getEntityByField(vehicles, 'deviceId', deviceId);
  },
  getVehicleIdByDeviceId(vehicles, deviceId) {
    const vehicle = this.getVehicleByDeviceId(vehicles, deviceId);

    return vehicle !== null ? (vehicle.id || null) : null;
  },
  getVehicleIdByLicensePlate(vehicles, licensePlate){
    const vehicle = this.getVehicleByLicensePlate(vehicles, licensePlate);
    return vehicle !== null ? (vehicle.id || null) : null;
  },
  getVehicleByLicensePlate(vehicles, licensePlate, formatFieldCallback){
    return this.getEntityByField(vehicles, 'licensePlate', licensePlate, formatFieldCallback);
  },
  getVehiclesByGroupId(devices, vehicles, groupId) {
    const deviceIds = this.getDevicesByGroupId(devices, groupId).map(device => device.id);

    return vehicles.filter(vehicle => deviceIds.includes(vehicle.deviceId));
  },
  mapDashboardDevices(devices, groups, vehicles, dashboardDeviceColumns, isGroupShown) {
    return devices?.map(item => {
      const result = {...item};
      dashboardDeviceColumns?.forEach(column => {
        const columnData = DashboardDeviceColumn.get(column);
        result[columnData.value] = columnData.callback?.(item, groups, vehicles) ?? item[columnData.value] ?? '';
      });

      if (isGroupShown && !dashboardDeviceColumns?.includes(DashboardDeviceColumn.Group)) {
        const columnData = DashboardDeviceColumn.get(DashboardDeviceColumn.Group);
        result[columnData.value] = columnData.callback?.(item, groups, vehicles) ?? item[columnData.value] ?? '';
      }

      return result;
    });
  },
  getModelByVehicle(models, vehicle) {
    if (vehicle === null) {
      return null;
    }

    return models.filter(model => model.id === vehicle.model)[0] || null;
  },
  getMakeByModel(makes, model) {
    if (model === null) {
      return null;
    }

    return makes.filter(make => make.id === model.make)[0] || null;
  },
  getGpsParamsByPosition(position) {
    return teltonika.parseAttributes(position['attributes'] || {});
  },
  getGoogleGeoByPosition(position) {
    return {lat: position.latitude, lng: position.longitude};
  },
  getPositionAttribute(position, attribute, defaultValue) {
    return tryAttribute(position, attribute, defaultValue);
  },
  getDriverByBeaconUUID(beaconUUID,drivers,noDriverLabel='no driver'){
    const driver = drivers.filter(driver => driver.uniqueId === beaconUUID)[0];
    return driver === undefined ? noDriverLabel : driver.name;
  },
  getDriverByPosition(position,ioAttribute,drivers){
    const beaconUUID = this.getPositionAttribute(position,ioAttribute);
    return this.getDriverByBeaconUUID(beaconUUID, drivers);
  },
  getAddressByPosition(position) {
    return position['address'] || `${position.latitude}, ${position.longitude}`;
  },
  getSpeedStatusByPosition(position) {
    const ignition = this.getPositionAttribute(position, 'ignition', true);

    if (ignition === false) {
      return 'no-ignition';
    }

    const motion = this.getPositionAttribute(position, 'motion', true);

    if (motion === false) {
      return 'no-motion';
    }

    return this.getSpeedInKmphByPosition(position);
  },
  getSpeedInKmphByPosition(position) {
    if (position['speed'] === undefined) {
      return 0;
    }

    return dataManipulator.knotToKm(position['speed']);
  },
  getFuelByPosition(position, tankCapacity) {
    tankCapacity = tankCapacity || process.env.VUE_APP_FALLBACK_TANK_CAPACITY_LITRES;

    const fuel = this.getPositionAttribute(position, 'fuel', null);
    const fuelLevelPercentage = this.getPositionAttribute(position, 'fuelLevelPercentage');
    const obdFuelLevelPercent = this.getPositionAttribute(position, 'io48');
    const canFuelLevelPercent = this.getPositionAttribute(position, 'io89');
    const obdOemFuelLevelTenthLitres = this.getPositionAttribute(position, 'io390');
    const canFuelLevelTenthLitres = this.getPositionAttribute(position, 'io84');

    const tryFuel = fuel || null;
    const tryFuelLevelPercentage = fuelLevelPercentage ? (fuelLevelPercentage / 100 * tankCapacity) : null;
    const tryObdFuelLevelPercent = obdFuelLevelPercent ? (obdFuelLevelPercent / 100 * tankCapacity) : null;
    const tryCanFuelLevelPercent = canFuelLevelPercent ? (canFuelLevelPercent / 100 * tankCapacity) : null;
    const tryObdOemFuelLevelTenthLitres = obdOemFuelLevelTenthLitres ? (obdOemFuelLevelTenthLitres * 0.1) : null;
    const tryCanFuelLevelTenthLitres = canFuelLevelTenthLitres ? (canFuelLevelTenthLitres * 0.1) : null;

    return tryFuel ||
        tryObdFuelLevelPercent ||
        tryFuelLevelPercentage ||
        tryCanFuelLevelPercent ||
        tryObdOemFuelLevelTenthLitres ||
        tryCanFuelLevelTenthLitres || '';
  },
  getFuelCapStatusByPosition(position) {
    // First check for the hardware fuel cap
    const fuelCapStatus = this.getPositionAttribute(position, 'io331', null);
    if (fuelCapStatus !== null) {
      switch (fuelCapStatus) {
        case '7375':
          return FuelCapStatus.Open;
        case '736c':
          return FuelCapStatus.Close;
        case '7275':
        case '726c':
          return FuelCapStatus.Tampering;
      }
    }

    // Check for beacon fuel cap
    const fuelCapBeacon = this.getPositionAttribute(position, 'io10808', null);
    if (fuelCapBeacon !== null) {
      switch (fuelCapBeacon) {
        case 0:
          return FuelCapStatus.Open;
        case 1:
          return FuelCapStatus.Close;
      }
    }

    return null;
  },
  getEngineTimeByPosition(position) {
    if (!position) {
      return null;
    }

    return this.getPositionAttribute(position, 'hours', null);
  },
  getEngineMinutesByPosition(position) {
    if (!position) {
      return null;
    }

    const milliseconds = this.getPositionAttribute(position, 'hours', null);
    if (milliseconds === null || milliseconds === undefined) {
      return null;
    }

    return dataManipulator.convertMillisecondsToMinutes(milliseconds);
  },
  formatEngineHours(milliseconds) {
    if (milliseconds == null) {
      return '-';
    }

    const hours = dataManipulator.millisecondsToTime(milliseconds);
    return i18n.global.t('reports.trips.result.engine-hours', hours);
  },
  getTimeByPosition(position) {
    const positionTime = position['fixTime'] || null;
    if (positionTime === null) {
      return null;
    }

    return dataManipulator.iso8601DateStringToLocalMoment(positionTime);
  },
  getTimeByReportStop(reportStop) {
    const stopAtTime = reportStop['startTime'] || null;
    if (stopAtTime === null) {
      return null;
    }

    return dataManipulator.iso8601DateStringToLocalMoment(stopAtTime);
  },
  getAddressByReportStop(reportStop) {
    return reportStop['address'] || `${reportStop.latitude}, ${reportStop.longitude}`;
  },
  getDurationByReportStop(reportStop) {
    const duration = reportStop['duration'] || null;
    if (duration === null) {
      return null;
    }

    const time = dataManipulator.millisecondsToTime(duration);
    return dataManipulator.formatDuration(time);
  },
  getGeoFenceName(id, geoFences) {
    if (!id) {
      return null;
    }

    return geoFences.find(x => x.id === id)?.name;
  },
  getMaintenanceName(id, maintenance) {
    if (!id) {
      return null;
    }

    return maintenance.find(x => x.id === id)?.name;
  },
  getLastNonSeenEventByDeviceIdAndSeverity(events, deviceId, severity = EventSeverity.information) {
    return events.filter(x => {
      const eventInfo = this.getEventInfo(x.type, x.attributes?.alarm);
      return x.isSeen === false && x.deviceId === deviceId && eventInfo.severity >= severity;
    })[0] ?? null;
  },
  hasNonSeenEvent(events) {
    return events.some(x => x.isSeen === false);
  },
  getEventInfo(type, subType) {
    let eventInfo = getEventInfoInternal(eventsInfo, type);

    if (!('message' in eventInfo)) {
      eventInfo = getEventInfoInternal(eventInfo, subType);
    }

    return eventInfo;
  },
  getMessageByEvent(type, alarmType, useFallbackMessage = false) {
    const eventInfo = this.getEventInfo(type, alarmType);

    if (useFallbackMessage) {
      return eventInfo.fallbackMessage ?? eventInfo.message;
    }

    return eventInfo.message;
  },
  getTranslatedMessageByEvent(type, alarmType, geoFenceName, maintenanceName, useFallbackMessage) {
    const message = this.getMessageByEvent(type, alarmType, useFallbackMessage);
    const translated = i18n.global.t(message);
    if (geoFenceName) {
      return translated + ' ' + geoFenceName;
    }

    if (maintenanceName) {
      return translated + ': ' + maintenanceName;
    }

    return translated;
  },
  getDeviceIgnition(deviceId, deviceIgnitions) {
    const ignitionData = deviceIgnitions.get(deviceId);
    if (!ignitionData) {
      return false;
    }

    /*if (!ignitionData.ignition) {
      const diffInSeconds = moment.duration(moment().diff(ignitionData?.timestamp)).asSeconds();
      if (diffInSeconds > 30) {
        return false;
      }
    }*/

    return ignitionData.ignition;
  },
  getStateByField(fieldStates, field) {
    return fieldStates[field] !== undefined ? fieldStates[field] : null;
  },
  shouldFetchViolations(company) {
    // Physical Person case
    if (company.trafficViolationsRequestType === TrafficViolationsRequestType.AsPhysicalPerson && company.legalEntityIdentityNumber) {
      return true;
    }
    // Company case
    else if (company.trafficViolationsRequestType === TrafficViolationsRequestType.AsCompany && company.pic) {
      return true;
    }

    return false;
  },
  getViolationsInfoHtml(company, licensePlate = null) {
    let text = '<div class="align-items-start cols">';
    switch (company.trafficViolationsRequestType) {
      case TrafficViolationsRequestType.AsPhysicalPerson: {
        text += `<div>${i18n.global.t('reports.violations.violations-request-type-as-physical')}:</div>`;
        break;
      }

      case TrafficViolationsRequestType.AsCompany: {
        text += `<div>${i18n.global.t('reports.violations.violations-request-type-as-company')}:</div>`;
        text += `<div>${i18n.global.t('settings.on-board.users.form.company-pic')}: <span class="font-bold">${company.pic}</span></div>`;
        break;
      }
    }

    text += `<div>${i18n.global.t('settings.on-board.users.form.legal-entity-identity-number')}: <span class="font-bold">${company.legalEntityIdentityNumber}</span></div>`;
    text += `<div>${i18n.global.t('settings.on-board.users.form.legal-entity-document-number')}: <span class="font-bold">${company.legalEntityDocumentNumber}</span></div>`;
    if (licensePlate) {
      text += `<div>${i18n.global.t('settings.on-board.devices.form.license-plate')}: <span class="font-bold">${licensePlate}</span></div>`;
    }
    text += '</div>';

    return text;
  },
  getTripsRecordPositionsWithStops(tripsRecords, tripsRecordPositions, allTripsPositions, mapRoutes = null) {
    if (mapRoutes?.length) {
      return mapRoutes.map(route => ({
        trip: route.trip ?? null,
        stops: [...(route.stops ?? [])],
        index: route.index,
        positions: route.positions ?? [],
        stay: route.stay ?? null,
      }));
    }

    return tripsRecordPositions.map((positions) => {

      const firstPosition = positions[0] ?? null;
      const posTime = new Date(firstPosition?.fixTime).getTime();

      const tripIndex = tripsRecords.findIndex(tripRecord => {
        const tripStartTime = new Date(tripRecord.startTime).getTime();
        const tripEndTime = new Date(tripRecord.endTime).getTime();
        return posTime >= tripStartTime && posTime <= tripEndTime;
      });
      const trip = tripsRecords[tripIndex];

      return {
        trip: trip ?? null,
        stops: [],
        index: tripIndex + 1,
        positions: positions,
        stay: null,
      };
    });
  },
  isEcoEventSupportedAlarm(alarmType) {
    switch (alarmType) {
      case 'laneChange':
        return false;

      default:
        return !!alarmType;
    }
  },
  isTaskFinished(task) {
    return task?.status === TaskStatus.Completed ||
        task?.status === TaskStatus.InCompleted ||
        new Date(task?.scheduleDate).getTime() < new Date().getTime();
  },
  isTaskInProgress(task) {
    return task?.status === TaskStatus.InProgress ||
        new Date(task?.startDate).getTime() < new Date().getTime();
  },
  compareTaskGeofences(firstGeofences, secondGeofences) {
    if (firstGeofences?.length !== secondGeofences?.length) {
      return false;
    }

    const firstGeofencesSorted = firstGeofences
        .map(x => { return {numbering: x.numbering, geofenceId: x.geofenceId}; })
        .sort((first, second) => first.numbering - second.numbering);
    const secondGeofencesSorted = secondGeofences
        .map(x => { return {numbering: x.numbering, geofenceId: x.geofenceId}; })
        .sort((first, second) => first.numbering - second.numbering);

    return dataAggregator.compareObjects(firstGeofencesSorted, secondGeofencesSorted);
  },
  isEmail(email){
    return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email);
  }
};

export const dataAggregator = {
  filterListByToken(list, fields, token) {
    if (!token) {
      return list;
    }

    const searchToken = token.toLowerCase();
    if (!searchToken) {
      return list;
    }

    return list.filter(item => {
      return fields.some(field => {
        if (typeof item[field] !== 'string') {
          return false;
        }

        return item[field].toLowerCase().includes(searchToken);
      });
    });
  },
  filterDevicesByToken(list, token) {
    return this.filterListByToken(list, DashboardDeviceColumn.all().map(item => item.value), token);
  },
  filterVehiclesByToken(list, devicesList, token) {
    const searchToken = token.toLowerCase();

    return list.filter(vehicle => {
      const vehicleDevice = entityDataManager.getDeviceByVehicle(devicesList, vehicle);

      let deviceMatches = false;
      if (vehicleDevice !== null) {
        deviceMatches = deviceMatches || vehicleDevice.name.toLowerCase().includes(searchToken);
        deviceMatches = deviceMatches || vehicleDevice.uniqueId.includes(searchToken);
      }

      return deviceMatches || vehicle.fullName.toLowerCase().includes(searchToken);
    });
  },
  filterGeoFencesByToken(list, token) {
    return this.filterListByToken(list, ['name', 'description'], token);
  },
  filterViolationsByLicensePlate(violations, licensePlate) {
    if (violations === null || licensePlate == null) {
      return null;
    }

    licensePlate = dataManipulator.formatLicensePlateToEnglishChars(licensePlate);
    return violations = violations.filter(x => x.licensePlate === licensePlate);
  },
  sortList(list, sortBy, sortType) {
    const listCopy = [...list];

    listCopy.sort(
        (a, b) => sortType === 'ASC'
            ? (a[sortBy] > b[sortBy] ? 1 : -1)
            : (a[sortBy] > b[sortBy] ? -1 : 1),
    );

    return listCopy;
  },
  extractFilenameFromContentDisposition(contentDispositionHeader) {
    return contentDispositionHeader
        .split(';')
        .find(n => n.includes('filename='))
        .replace('filename=', '')
        .replaceAll('"', '')
        .trim();
  },
  compareObjects(first, second) {
    return JSON.stringify(first) === JSON.stringify(second);
  }
};

export const GoogleMapsManager = {
  core: null,
  places: null,
  marker: null,
  geometry: null,

  defaultCenter: {lat: 43.204777 , lng: 27.910145},

  async loadGoogleMaps() {
    await Promise.all([
      gmLoader.importLibrary('core').then(x => this.core = x),
      gmLoader.importLibrary('places').then(x => this.places = x),
      gmLoader.importLibrary('marker').then(x => this.marker = x),
      gmLoader.importLibrary('geometry').then(x => this.geometry = x)
    ]);
  },
  getMapBoundsByPositions(googleMapsPositions) {
    const newBoundary = new window.google.maps.LatLngBounds();

    for (let geoPos of googleMapsPositions) {
      newBoundary.extend(geoPos);
    }

    return newBoundary;
  },
  removeFromMap(object) {
    object?.setVisible(false);
    object?.unbindAll();
    object?.setMap(null);
  },
  drawGeoFence(googleMaps, geoFence, map, clickHandler = null) {
    if (geoFence['type'] === GeoFenceType.Circle) {
      return this.drawCircleGeoFence(googleMaps, geoFence, map, clickHandler);
    }
  },
  drawCircleGeoFence(googleMaps, geoFence, map, clickHandler = null) {
    const area = new googleMaps.Circle({
      ...this.geoFenceBase,
      ...this.getGeoFenceColorOptions(geoFence.attributes?.color ?? null),
      map: map
    });

    this.setGeoFenceProperties(area, geoFence);

    if (clickHandler) {
      area.addListener('click', () => {
        clickHandler(geoFence);
      });
    }

    return area;
  },
  setGeoFenceProperties(area, geoFenceData) {
    if (geoFenceData['type'] === GeoFenceType.Circle) {
      this.setCircleGeoFenceProperties(area, geoFenceData);
    }
  },
  setCircleGeoFenceProperties(area, geoFenceData) {
    area.setRadius(geoFenceData['area']['radius']);
    area.setCenter(geoFenceData['area']['coordinates']);
  },
  setGeoFenceEditable(area, boundsChangedHandler) {
    area.setDraggable(true);
    area.setEditable(true);

    if (boundsChangedHandler) {
      area.addListener('bounds_changed', () => boundsChangedHandler());
    }
  },
  setGeoFenceReadOnly(area, googleMaps = null) {
    area.setDraggable(false);
    area.setEditable(false);
    googleMaps?.event?.clearListeners(area, 'bounds_changed');
  },
  createGeoFenceInfoWindow(geoFence) {
    switch (geoFence['type']) {
      case GeoFenceType.Circle:
        return this.createCircleGeoFenceInfoWindow(geoFence);
    }
  },
  createCircleGeoFenceInfoWindow(geoFence) {
    const props = {
      title: geoFence.name,
      description: geoFence.description,
      coordinates: geoFence.area.coordinates,
      radius: geoFence.area.radius
    };

    return createApp(GeoFenceInfo, props).use(i18n);
  },
  showInfoWindow(googleInfoWindow, component, coordinates, map, googleMapsApi) {
    googleInfoWindow = this.prepareGoogleInfoWindow(googleInfoWindow, googleMapsApi);
    this.setInfoWindowContent(googleInfoWindow, component);
    googleInfoWindow.setPosition(coordinates);
    googleInfoWindow.open(map);

    return googleInfoWindow;
  },
  setInfoWindowContent(googleInfoWindow, component) {
    const wrapper = document.createElement('div');
    component.mount(wrapper);
    this.unmountInnerComponentOnCloseClick(googleInfoWindow, component);
    googleInfoWindow.setContent(wrapper);
  },
  prepareGoogleInfoWindow(googleInfoWindow, googleMaps) {
    googleInfoWindow?.close();
    googleInfoWindow = this.createInfoWindow(googleMaps);

    return googleInfoWindow;
  },
  createInfoWindow(googleMaps) {
    return new googleMaps.InfoWindow();
  },
  closeInfoWindow(googleInfoWindow, component) {
    this.unmountComponent(component);
    googleInfoWindow?.close();
  },
  unmountInnerComponentOnCloseClick(googleInfoWindow, component) {
    googleInfoWindow.addListener('closeclick', () => this.unmountComponent(component));
  },
  unmountComponent(component) {
    component?.unmount();
  },
  getGoogleGeoByReportStop(reportStop) {
    return {lat: reportStop.latitude, lng: reportStop.longitude};
  },
  getCoordinatesFromGoogleData(data) {
    return {lat: parseFloat(data.lat().toPrecision(8)), lng: parseFloat(data.lng().toPrecision(8))};
  },
  convertUserGeoFenceToAreaOptions(geoFenceData) {
    if (geoFenceData['type'] === GeoFenceType.Circle) {
      return this.convertUserGeoFenceToCircleOptions(geoFenceData);
    }
  },
  convertUserGeoFenceToCircleOptions(geoFenceData) {
    let options = {
      ...this.geoFenceBase,
      radius: geoFenceData['area']['radius'],
      center: geoFenceData['area']['coordinates'],
    };

    const color = geoFenceData['attributes']['color'];
    if(color) {
      options = {...options, ...this.getGeoFenceColorOptions(color)};
    }

    return options;
  },
  getGeoFenceColorOptions(color) {
    switch (color) {
      case GeoFenceColor.Red:
        return {
          strokeColor: "#ff0014",
          fillColor: "#ff1717",
          fillOpacity: 0.7,
        };
      case GeoFenceColor.Yellow:
        return {
          strokeColor: "#f1e732",
          fillColor: "#ece62f",
          fillOpacity: 0.7,
        };
      case GeoFenceColor.Blue:
        return {
          strokeColor: "#002aff",
          fillColor: "#173eff",
          fillOpacity: 0.7,
        };
      case GeoFenceColor.Orange:
        return {
          strokeColor: "#ff6a00",
          fillColor: "#FF6A00FF",
          fillOpacity: 0.7,
        }
      case GeoFenceColor.Brown:
        return {
          strokeColor: "#693600",
          fillColor: "#693600FF",
          fillOpacity: 0.7,
        }
      case GeoFenceColor.Green:
        return {
          strokeColor: "#3f9e65",
          fillColor: "#339c90",
          fillOpacity: 0.3
        }
      default:
        return {};
    }
  },
  openLocationLink(latitude, longitude) {
    const link = this.getLocationLink(latitude, longitude);
    window.open(link, '_blank', 'noreferrer');
  },
  getLocationLink(latitude, longitude) {
    return `https://www.google.com/maps/place/${latitude},${longitude}`;
  },
  geoFenceBase: {
    strokeColor: "#3f9e65",
    strokeOpacity: 0.8,
    strokeWeight: 2,
    fillColor: "#339c90",
    fillOpacity: 0.3,
    zIndex: 100
  },
}

export const WorkflowManager = {
  setTripsFilters(deviceId, fromDate, toDate, includeAddresses = true) {
    const from = dataManipulator.dateTimeToString(fromDate);
    const to = dataManipulator.dateTimeToString(toDate);

    store.commit(mutations.SET_TRIPS_FILTER_DATE_FROM, {date: from});
    store.commit(mutations.SET_TRIPS_FILTER_DATE_TO, {date: to});
    store.commit(mutations.SET_TRIPS_FILTER_DEVICE_ID, {deviceId: deviceId});
    store.commit(mutations.SET_TRIPS_FILTER_DRIVER, null);
    store.commit(mutations.SET_TRIPS_FILTER_GROUP, null);
    store.commit(mutations.SET_TRIPS_FILTER_INCLUDE_ADDRESSES, includeAddresses);
  },
  getTripsPositionsPayload(trips, fallbackDeviceId, specificTime = null) {
    let payload = (trips ?? []).map(route => ({
      deviceId: route.deviceId,
      from: route.startTime,
      to: route.endTime,
    }));

    if (payload.length <= 0 && fallbackDeviceId && specificTime) {
      payload = [{
        deviceId: fallbackDeviceId,
        from: specificTime,
        to: specificTime,
      }];
    }

    return payload;
  },
  async loadTripsMapDataFromCurrentFilters(specificTime = null) {
    const tripsData = await store.dispatch(actions.GET_TRIPS_DATA, {includeMapData: 1});
    store.commit(mutations.SET_TRIPS_DATA, tripsData);
    const trips = tripsData?.trips ?? [];
    const backendTripsRecordPositions = dataManipulator.getTripsRecordPositionsFromMapData(tripsData);
    if (backendTripsRecordPositions.length > 0) {
      store.commit(mutations.SET_TRIPS_RECORD_POSITIONS, backendTripsRecordPositions);

      return {
        tripsData: store.state.tripsData,
        tripsRecordPositions: store.state.tripsRecordPositions ?? [],
      };
    }

    const fallbackDeviceId = store.state.tripsFilterDevice?.deviceId ?? null;
    const payload = this.getTripsPositionsPayload(trips, fallbackDeviceId, specificTime);

    if (payload.length > 0) {
      await store.dispatch(actions.GET_TRIPS_RECORD_POSITIONS_WITH_STOPS, payload);
    }

    return {
      tripsData: store.state.tripsData,
      tripsRecordPositions: store.state.tripsRecordPositions ?? [],
    };
  },
  async loadTripsMapData(deviceId, fromDate, toDate, specificTime = null) {
    this.setTripsFilters(deviceId, fromDate, toDate, true);
    return await this.loadTripsMapDataFromCurrentFilters(specificTime);
  },
  async getTripsMapDataWithoutStore(deviceId, fromDate, toDate, specificTime = null, includeAddresses = true) {
    const from = dataManipulator.dateTimeToString(fromDate);
    const to = dataManipulator.dateTimeToString(toDate);
    const tripsData = await store.dispatch(actions.GET_TRIPS_DATA, {
      from,
      to,
      deviceId,
      addresses: includeAddresses ? 1 : 0,
      includeMapData: 1,
    });
    const trips = tripsData?.trips ?? [];
    const backendTripsRecordPositions = dataManipulator.getTripsRecordPositionsFromMapData(tripsData);
    if (backendTripsRecordPositions.length > 0) {
      return {
        tripsData,
        tripsRecordPositions: backendTripsRecordPositions,
      };
    }

    const payload = this.getTripsPositionsPayload(trips, deviceId, specificTime);

    if (payload.length <= 0) {
      return {
        tripsData,
        tripsRecordPositions: [],
      };
    }
    const tripsRecordPositions = await store.dispatch(actions.GET_TRIPS_RECORD_POSITIONS_WITH_STOPS, {
      payload,
      skipStore: true,
    });

    return {
      tripsData,
      tripsRecordPositions,
    };
  },
  openTripsPage(deviceId, fromDate, toDate) {
    this.setTripsFilters(deviceId, fromDate, toDate, true);

    router.push({path: '/reports/trips', query: {search: 1}});
  },
  async openTripsMaps(deviceId, fromDate, toDate, specificTime) {
    await this.loadTripsMapData(deviceId, fromDate, toDate, specificTime);

    await router.push({path: '/reports/eco-events', query: {specificTime: specificTime}});
  },
  openTripsMapsInNewPage(deviceId, fromDate, toDate, specificTime) {
    const from = dataManipulator.dateTimeToString(fromDate);
    const to = dataManipulator.dateTimeToString(toDate);
    const routeData = router.resolve({
      path: '/reports/eco-events',
      query: {
        search: '1',
        dateFrom: from,
        dateTo: to,
        specificTime: specificTime,
        deviceId: deviceId,
      }
    });
    window.open(routeData.href, '_blank', 'noopener,noreferrer');
  }
}
